// Commercial settings for platform administrators (Phase 8): the prices and
// the individual trial length in force, one company's plan and negotiated
// prices, and every company's calls this month with what was billed. Saved
// prices override the brand pack's pricing; a new trial length applies to
// trials that start afterwards.
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  NativeSelect,
  NumberInput,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { saveBlob } from "../../shared/ui/download";
import { majorUnits, parsePrice, useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { billingMonths, monthRange } from "../../shared/ui/months";
import { AdminLayout } from "./AdminLayout";
import { CompanyPicker } from "./CompanyPicker";

type Settings = Schemas["BillingSettings"];
type Terms = Schemas["CompanyTerms"];
type Plan = Terms["plan"];
type BandRow = { upTo: string; price: string };

const SETTINGS_KEY = ["admin", "billing", "settings"] as const;
const MAX_BANDS = 10;

export function AdminBillingPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")}>
      <Stack gap="lg">
        <PricingSettings />
        <CompanyTermsSection />
        <CallReport />
      </Stack>
    </AdminLayout>
  );
}

function PricingSettings() {
  const { t } = useTranslation();
  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => unwrap(api.GET("/admin/billing/settings")) });
  return (
    <Paper withBorder p="md" radius="md" data-testid="billing-settings">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("admin_billing.settings_title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("admin_billing.settings_intro")}
          </Text>
        </Stack>
        {settings.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : settings.isError ? (
          <FormError message={errorMessage(settings.error, t("error.generic"))} />
        ) : (
          <SettingsForm settings={settings.data} />
        )}
      </Stack>
    </Paper>
  );
}

function SettingsForm({ settings: s }: { settings: Settings }) {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const currency = s.currency;
  const [trialDays, setTrialDays] = useState<number | string>(s.individual_trial_days);
  const [individual, setIndividual] = useState(majorUnits(s.individual_price_minor, currency));
  const [stk, setStk] = useState(majorUnits(s.stk_price_minor, currency));
  const [monthly, setMonthly] = useState(majorUnits(s.monthly_fee_minor, currency));
  const [bands, setBands] = useState<BandRow[]>(() =>
    s.resolve_bands.map((b) => ({ upTo: b.up_to ? String(b.up_to) : "", price: majorUnits(b.price_minor, currency) })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const setBand = (i: number, patch: Partial<BandRow>) =>
    setBands((bs) => bs.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const days = typeof trialDays === "number" ? trialDays : Number.parseInt(trialDays, 10);
    const prices = [individual, stk, monthly].map((v) => parsePrice(v, currency));
    const resolve = bands.map((b, i) => ({
      up_to: i === bands.length - 1 ? 0 : Number.parseInt(b.upTo, 10),
      price_minor: parsePrice(b.price, currency),
    }));
    if (!Number.isInteger(days) || days < 0 || days > 365) return setError(t("admin_billing.trial_invalid"));
    if (prices.some((p) => p === null) || resolve.some((b) => b.price_minor === null))
      return setError(t("admin_billing.price_invalid"));
    for (let i = 0; i < resolve.length - 1; i++) {
      const up = resolve[i]?.up_to ?? 0;
      const prev = i > 0 ? (resolve[i - 1]?.up_to ?? 0) : 0;
      if (!Number.isInteger(up) || up <= prev) return setError(t("admin_billing.bands_invalid"));
    }
    const [individualMinor, stkMinor, monthlyMinor] = prices as [number, number, number];
    setBusy(true);
    const { data, error } = await api.PUT("/admin/billing/settings", {
      body: {
        individual_trial_days: days,
        individual_price_minor: individualMinor,
        stk_price_minor: stkMinor,
        monthly_fee_minor: monthlyMinor,
        resolve_bands: resolve.map((b) => ({ up_to: b.up_to, price_minor: b.price_minor ?? 0 })),
      },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData(SETTINGS_KEY, data);
    void queryClient.invalidateQueries({ queryKey: ["public", "pricing"] });
    setSaved(true);
  };

  const priceInput = (label: string, hint: string, value: string, set: (v: string) => void) => (
    <TextInput
      label={label}
      description={hint}
      value={value}
      onChange={(e) => set(e.currentTarget.value)}
      inputMode="decimal"
      leftSection={<Text size="xs">{currency}</Text>}
      leftSectionWidth={48}
      required
    />
  );

  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <Stack gap="md">
        <Title order={3} size="h5">
          {t("admin_billing.individuals")}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <NumberInput
            label={t("admin_billing.trial_days")}
            description={t("admin_billing.trial_days_hint")}
            value={trialDays}
            onChange={setTrialDays}
            min={0}
            max={365}
            allowDecimal={false}
            allowNegative={false}
            suffix={` ${t("admin_billing.days")}`}
            required
          />
          {priceInput(
            t("admin_billing.individual_price"),
            t("admin_billing.individual_price_hint"),
            individual,
            setIndividual,
          )}
        </SimpleGrid>

        <Title order={3} size="h5">
          {t("admin_billing.companies")}
        </Title>
        <Text size="sm" c="dimmed">
          {t("admin_billing.bands_hint")}
        </Text>
        <Stack gap="xs" data-testid="price-bands">
          {bands.map((b, i) => {
            const last = i === bands.length - 1;
            const from = i === 0 ? 1 : Number.parseInt(bands[i - 1]?.upTo ?? "0", 10) + 1;
            return (
              <Group key={i} align="flex-end" gap="sm" wrap="wrap">
                {last ? (
                  <Text size="sm" w={200} pb={8}>
                    {t("admin_billing.band_rest", { from: Number.isFinite(from) ? from : 1 })}
                  </Text>
                ) : (
                  <TextInput
                    w={200}
                    label={t("admin_billing.band_up_to", { n: i + 1 })}
                    value={b.upTo}
                    onChange={(e) => setBand(i, { upTo: e.currentTarget.value })}
                    inputMode="numeric"
                    required
                  />
                )}
                <TextInput
                  w={200}
                  label={t("admin_billing.band_price", { n: i + 1 })}
                  value={b.price}
                  onChange={(e) => setBand(i, { price: e.currentTarget.value })}
                  inputMode="decimal"
                  leftSection={<Text size="xs">{currency}</Text>}
                  leftSectionWidth={48}
                  required
                />
                {bands.length > 1 && (
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    mb={6}
                    onClick={() => setBands((bs) => bs.filter((_, j) => j !== i))}
                    aria-label={t("admin_billing.band_remove", { n: i + 1 })}
                  >
                    {t("common.remove")}
                  </Button>
                )}
              </Group>
            );
          })}
          {bands.length < MAX_BANDS && (
            <Button
              variant="subtle"
              size="xs"
              w="fit-content"
              onClick={() =>
                // A new band goes before the open-ended last one, at its price.
                setBands((bs) => {
                  const last = bs[bs.length - 1] ?? { upTo: "", price: "0" };
                  return [...bs.slice(0, -1), { upTo: "", price: last.price }, last];
                })
              }
            >
              {t("admin_billing.band_add")}
            </Button>
          )}
        </Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {priceInput(t("admin_billing.stk_price"), t("admin_billing.stk_price_hint"), stk, setStk)}
          {priceInput(t("admin_billing.monthly_fee"), t("admin_billing.monthly_fee_hint"), monthly, setMonthly)}
        </SimpleGrid>
        {saved && <Notice>{t("admin_billing.saved")}</Notice>}
        <FormError message={error} />
        <Group gap="md">
          <Button type="submit" loading={busy}>
            {t("admin_billing.save")}
          </Button>
          {s.updated_at && (
            <Text size="xs" c="dimmed">
              {t("admin_settings.updated", { date: f.dateTime(s.updated_at) })}
            </Text>
          )}
        </Group>
      </Stack>
    </form>
  );
}

function CompanyTermsSection() {
  const { t } = useTranslation();
  const [companyId, setCompanyId] = useState("");
  const terms = useQuery({
    queryKey: ["admin", "billing", "terms", companyId],
    enabled: companyId !== "",
    queryFn: () =>
      unwrap(api.GET("/admin/companies/{company_id}/billing", { params: { path: { company_id: companyId } } })),
  });
  return (
    <Paper withBorder p="md" radius="md" data-testid="company-terms">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("admin_billing.terms_title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("admin_billing.terms_intro")}
          </Text>
        </Stack>
        <CompanyPicker value={companyId} onChange={(c) => setCompanyId(c?.id ?? "")} />
        {companyId &&
          (terms.isPending ? (
            <Loader size="sm" aria-label={t("common.loading")} />
          ) : terms.isError ? (
            <FormError message={errorMessage(terms.error, t("error.generic"))} />
          ) : (
            <TermsForm key={companyId} terms={terms.data} />
          ))}
      </Stack>
    </Paper>
  );
}

function TermsForm({ terms }: { terms: Terms }) {
  const { t } = useTranslation();
  const f = useFormat();
  const { currency } = useBrand();
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState<Plan>(terms.plan);
  const [ownPrice, setOwnPrice] = useState(terms.resolve_price_minor !== undefined);
  const [price, setPrice] = useState(
    terms.resolve_price_minor !== undefined ? majorUnits(terms.resolve_price_minor, currency) : "",
  );
  const [ownFee, setOwnFee] = useState(terms.monthly_fee_minor !== undefined);
  const [fee, setFee] = useState(majorUnits(terms.monthly_fee_minor ?? terms.effective_monthly_fee_minor, currency));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const current = terms;
  const bands = current.effective_resolve_bands;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const resolve = ownPrice ? parsePrice(price, currency) : undefined;
    const monthly = plan === "monthly" && ownFee ? parsePrice(fee, currency) : undefined;
    if (resolve === null || monthly === null) return setError(t("admin_billing.price_invalid"));
    setBusy(true);
    const { data, error } = await api.PUT("/admin/companies/{company_id}/billing", {
      params: { path: { company_id: terms.company_id } },
      body: {
        plan,
        ...(resolve !== undefined ? { resolve_price_minor: resolve } : {}),
        ...(monthly !== undefined ? { monthly_fee_minor: monthly } : {}),
      },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData(["admin", "billing", "terms", terms.company_id], data);
    void queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
    setNotice(t("admin_billing.terms_saved"));
  };

  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <Stack gap="md">
        <Text size="sm" data-testid="terms-current">
          {t("admin_billing.terms_current", {
            plan: t(`billing.plan_${current.plan}`),
            price:
              bands.length === 1 && bands[0]
                ? f.money(bands[0].price_minor, currency)
                : t("admin_billing.terms_bands", { count: bands.length }),
          })}
        </Text>
        <Stack gap={4}>
          <Text size="sm" fw={500} id="terms-plan">
            {t("admin_billing.plan")}
          </Text>
          <SegmentedControl
            aria-labelledby="terms-plan"
            value={plan}
            onChange={(v) => setPlan(v as Plan)}
            data={(["prepaid", "postpaid", "monthly"] as const).map((p) => ({
              value: p,
              label: t(`admin_billing.plan_${p}`),
            }))}
          />
          <Text size="xs" c="dimmed">
            {t(`admin_billing.plan_${plan}_hint`)}
          </Text>
        </Stack>
        {plan === "monthly" && (
          <Stack gap="xs">
            <Checkbox
              label={t("admin_billing.own_fee", { fee: f.money(terms.effective_monthly_fee_minor, currency) })}
              checked={ownFee}
              onChange={(e) => setOwnFee(e.currentTarget.checked)}
            />
            {ownFee && (
              <TextInput
                maw={260}
                label={t("admin_billing.monthly_fee")}
                value={fee}
                onChange={(e) => setFee(e.currentTarget.value)}
                inputMode="decimal"
                leftSection={<Text size="xs">{currency}</Text>}
                leftSectionWidth={48}
                required
              />
            )}
          </Stack>
        )}
        {plan !== "monthly" && (
          <Stack gap="xs">
            <Checkbox
              label={t("admin_billing.own_price")}
              checked={ownPrice}
              onChange={(e) => setOwnPrice(e.currentTarget.checked)}
            />
            {ownPrice && (
              <TextInput
                maw={260}
                label={t("admin_billing.price_per_call")}
                value={price}
                onChange={(e) => setPrice(e.currentTarget.value)}
                inputMode="decimal"
                leftSection={<Text size="xs">{currency}</Text>}
                leftSectionWidth={48}
                required
              />
            )}
          </Stack>
        )}
        {notice && <Notice>{notice}</Notice>}
        <FormError message={error} />
        <Button type="submit" w="fit-content" loading={busy}>
          {t("admin_billing.terms_save")}
        </Button>
      </Stack>
    </form>
  );
}

function CallReport() {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const months = useMemo(() => billingMonths(), []);
  const [selected, setSelected] = useState(months[0]?.value ?? "");
  const month = months.find((m) => m.value === selected) ?? months[0];
  const range = month ? monthRange(month) : { from: "" };
  const report = useQuery({
    queryKey: ["admin", "reports", "calls", selected],
    queryFn: () => unwrap(api.GET("/admin/reports/calls", { params: { query: range } })),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const n = (v: number) => v.toLocaleString(i18n.language);
  const monthLabel = (value: string) => {
    const m = months.find((x) => x.value === value);
    return m
      ? new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric", timeZone: "UTC" }).format(
          new Date(Date.UTC(m.year, m.month, 1)),
        )
      : value;
  };
  const needle = q.trim().toLowerCase();
  const items = (report.data?.items ?? []).filter((r) => !needle || r.name.toLowerCase().includes(needle));
  const currency = report.data?.currency ?? "";
  const total = items.reduce(
    (a, r) => ({ calls: a.calls + r.calls, billable: a.billable + r.billable, amount: a.amount + r.amount_minor }),
    { calls: 0, billable: 0, amount: 0 },
  );

  const download = async () => {
    setError(null);
    setBusy(true);
    const { data, error } = await api.GET("/admin/reports/calls/export", { params: { query: range }, parseAs: "blob" });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    saveBlob(`calls-${selected}.csv`, data);
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="call-report">
      <Stack gap="sm">
        <Stack gap={4}>
          <Title order={2} size="h4">
            {t("admin_billing.report_title")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("admin_billing.report_intro")}
          </Text>
        </Stack>
        <Group align="flex-end" gap="sm" wrap="wrap">
          <NativeSelect
            label={t("billing.month")}
            value={selected}
            onChange={(e) => setSelected(e.currentTarget.value)}
            data={months.map((m) => ({ value: m.value, label: monthLabel(m.value) }))}
          />
          <TextInput label={t("admin_billing.report_filter")} value={q} onChange={(e) => setQ(e.currentTarget.value)} />
          <Button variant="light" loading={busy} onClick={() => void download()}>
            {t("billing.download_csv")}
          </Button>
        </Group>
        <FormError message={error} />
        {report.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : report.isError ? (
          <FormError message={errorMessage(report.error, t("error.generic"))} />
        ) : items.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="report-empty">
            {t("admin_billing.report_empty")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={860}>
            <Table striped verticalSpacing="xs" aria-label={t("admin_billing.report_title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("admin_companies.name")}</Table.Th>
                  <Table.Th>{t("admin_billing.plan")}</Table.Th>
                  <Table.Th ta="right">{t("calls.calls")}</Table.Th>
                  <Table.Th ta="right">{t("calls.disclosed")}</Table.Th>
                  <Table.Th ta="right">{t("admin_billing.not_found")}</Table.Th>
                  <Table.Th ta="right">{t("admin_billing.no_consent")}</Table.Th>
                  <Table.Th ta="right">{t("admin_billing.blocked")}</Table.Th>
                  <Table.Th ta="right">{t("calls.billable")}</Table.Th>
                  <Table.Th ta="right">{t("billing.amount")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((r) => (
                  <Table.Tr key={r.company_id} data-testid="report-row">
                    <Table.Td>{r.name || t("admin_credit.unknown_company")}</Table.Td>
                    <Table.Td>
                      <Badge variant="default">{t(`billing.plan_${r.plan}`, { defaultValue: r.plan })}</Badge>
                    </Table.Td>
                    <Table.Td ta="right">{n(r.calls)}</Table.Td>
                    <Table.Td ta="right">{n(r.disclosed)}</Table.Td>
                    <Table.Td ta="right">{n(r.not_found)}</Table.Td>
                    <Table.Td ta="right">{n(r.no_consent)}</Table.Td>
                    <Table.Td ta="right">{n(r.blocked)}</Table.Td>
                    <Table.Td ta="right">{n(r.billable)}</Table.Td>
                    <Table.Td ta="right">{f.money(r.amount_minor, currency)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              <Table.Tfoot>
                <Table.Tr data-testid="report-total">
                  <Table.Th colSpan={2}>{t("billing.total")}</Table.Th>
                  <Table.Th ta="right">{n(total.calls)}</Table.Th>
                  <Table.Th colSpan={4} />
                  <Table.Th ta="right">{n(total.billable)}</Table.Th>
                  <Table.Th ta="right">{f.money(total.amount, currency)}</Table.Th>
                </Table.Tr>
              </Table.Tfoot>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}
